'use client'

/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import React, { PropsWithChildren } from 'react'
import { Box } from '@mui/material'
import { Swiper as SwiperInstance } from 'swiper'
import { Navigation, Pagination, A11y, Autoplay } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/effect-fade'
import { Swiper } from 'swiper/react'
import { SwiperOptions } from 'swiper/types'
import { Icon } from '@media-components'
import { ActionButton } from '@components/common'
import { getStyles, CustomStyles } from '@utils/styles'
import { carouselMapper, type CarouselMapperKey } from './carousel.config'
import defaultStyles, { StyleClassNames } from './styles'

export type CarouselSectionData = PropsWithChildren

export type CarouselSectionProps = CarouselSectionData & {
  csx?: CustomStyles<StyleClassNames>
  swiperOptions?: CarouselMapperKey
  realSwiperOptions?: SwiperOptions
  disableArrow?: boolean
  darkBg?: boolean
  /** Full-width slides (e.g. resource cards). Default shows two slides on md+. */
  fullWidthSlides?: boolean
}

export function CarouselSection({
  csx,
  children,
  swiperOptions,
  realSwiperOptions,
  disableArrow = false,
  darkBg = false,
  fullWidthSlides = false,
}: CarouselSectionProps) {
  const swiperRef = React.useRef<SwiperInstance | null>(null)
  const styles = getStyles(defaultStyles, csx)
  const showArrows = !disableArrow

  const slidePrev = () => swiperRef.current?.slidePrev()
  const slideNext = () => swiperRef.current?.slideNext()

  const sideNavButtonSx = styles('sideNavButton').sx

  const renderSideNavButton = (direction: 'prev' | 'next') => (
    <ActionButton
      sx={sideNavButtonSx}
      onClick={direction === 'prev' ? slidePrev : slideNext}
      aria-label={direction === 'prev' ? 'Previous slide' : 'Next slide'}
    >
      <Icon
        iconName="ArrowRight"
        sx={{
          zIndex: 2,
          transform: direction === 'prev' ? 'rotate(180deg)' : 'none',
        }}
        className="arrowIcon"
        fontSize="medium"
        colorValues={darkBg ? 'common.white' : 'grey.800'}
      />
    </ActionButton>
  )

  const swiper = (
    <Swiper
      onSwiper={(swiperInstance) => {
        swiperRef.current = swiperInstance
      }}
      spaceBetween={fullWidthSlides ? 0 : 24}
      modules={[Navigation, Pagination, A11y, Autoplay]}
      navigation={false}
      pagination={{ clickable: true }}
      slidesPerView={fullWidthSlides ? 1 : undefined}
      {...(swiperOptions ? carouselMapper[swiperOptions] : {})}
      {...realSwiperOptions}
    >
      {children}
    </Swiper>
  )

  const sideNavOverlayBase = fullWidthSlides
    ? 'sideNavOverlaySlideAligned'
    : 'sideNavOverlay'

  return (
    <Box {...styles(fullWidthSlides ? 'wrapperSideNav' : 'wrapper')}>
      <Box
        {...styles(
          fullWidthSlides ? 'carouselOverlayAutoHeight' : 'carouselOverlay'
        )}
      >
        <Box
          {...styles(
            fullWidthSlides ? 'slidesViewportFullWidth' : 'slidesViewport'
          )}
        >
          <Box
            {...styles(
              fullWidthSlides ? 'swiperClipFullWidth' : 'swiperContainer'
            )}
          >
            {swiper}
          </Box>
          {showArrows && (
            <>
              <Box {...styles([sideNavOverlayBase, 'sideNavOverlayPrev'])}>
                {renderSideNavButton('prev')}
              </Box>
              <Box {...styles([sideNavOverlayBase, 'sideNavOverlayNext'])}>
                {renderSideNavButton('next')}
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}
